import React, { useEffect, useState } from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { GridItem, Icon } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IconButtonMenu from '../../../../../../components/IconButtonMenu';
import Row from '../../../../../../components/Row';
import RowGridColumn from '../../../../../../components/Row/components/RowGridColumn';
import LoadingSpinner from '../../../../../../components/Spinner';
import { TruncatedTextCell } from '../../../../../../components/Text/TruncatedTextCell';
import { analyticsIds } from '../../../../../../constants/analyticsIds';
import {
  resetAdminPageCurrentDepartment,
  resetMoveDepartmentData,
  selectAdminPageCurrentDepartmentData,
  selectMoveDepartmentData
} from '../../../../../../redux/modules/adminPage';
import { selectAreCompanyStatsLoading } from '../../../../../../redux/modules/company';
import { selectModalOpen, showModal } from '../../../../../../redux/modules/modal';
import { hideTray, showTray } from '../../../../../../redux/modules/tray';
import ConfirmDeleteModalDepartment from '../../../../components/modals/ConfirmDeleteModalDepartment';
import { activeMenuOptions, deactivatedMenuOptions } from '../../menuOptions';
import DepartmentForm from '../DepartmentForm';

const DepartmentRow = ({ department, currentDepartmentStats }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const areStatsLoading = useSelector(selectAreCompanyStatsLoading);
  const hasData = !!department.jobs.length;
  const moveDepartment = useSelector(selectMoveDepartmentData);
  const currentDepartment = useSelector(selectAdminPageCurrentDepartmentData);
  const modalOpen = useSelector(selectModalOpen);
  const menuOptions = department.archivedAt ? deactivatedMenuOptions(department, hasData) : activeMenuOptions(department, hasData);
  const [checkForDataTransfer, setCheckForDataTransfer] = useState(true);
  const [moveDepartmentSuccess, setMoveDepartmentSuccess] = useState(moveDepartment?.success);
  const [moveDepartmentSkipped, setMoveDepartmentSkipped] = useState(moveDepartment?.skipped);

  useEffect(() => {
    setMoveDepartmentSuccess(moveDepartment?.success);
    setMoveDepartmentSkipped(moveDepartment?.skipped);
  }, [modalOpen, moveDepartment.success, moveDepartment.skipped]);

  useEffect(() => {
    const handleChangeModal = () => {
      setCheckForDataTransfer(false);
      dispatch(showModal(() => (
        <ConfirmDeleteModalDepartment
          checkForDataTransfer={false}
          data={currentDepartment}
          departmentMoved={moveDepartmentSuccess}
          hasData={!!currentDepartment?.jobs?.length}
        />
      ), {
        actionRequired: true,
        withHistory: true,
        size: 'lg',
        title: 'Delete Department?',
        onClose: () => {
          dispatch(resetMoveDepartmentData());
          dispatch(resetAdminPageCurrentDepartment());
        }
      }));
    };

    if ((moveDepartmentSuccess || moveDepartmentSkipped) && checkForDataTransfer && currentDepartment) {
      handleChangeModal();
    }
  }, [checkForDataTransfer, currentDepartment, dispatch, hasData, moveDepartmentSkipped,
    moveDepartmentSuccess]);

  const showDepartmentTray = e => {
    e.preventDefault();
    dispatch(showTray(
      () => (
        <DepartmentForm department={department} onClose={() => dispatch(hideTray())} />
      ),
      {
        placement: 'right',
        onClose: () => dispatch(hideTray()),
        params: { companyId }
      }
    ));
  };

  return (
    <Row
      clickable={!department.archivedAt}
      columns="repeat(17, 1fr)"
      dusk={analyticsIds.createUniqueSelector(
        department.departmentName,
        analyticsIds.adminSettingsPage.departments.editDepartmentButton
      )}
      gap={4}
      layerStyle={department.archivedAt ? 'secondaryArchived' : 'row'}
      textAlign="center"
      onClick={!department.archivedAt && showDepartmentTray}
    >
      <GridItem colSpan={5} height="100%">
        <RowGridColumn align="flex-start" label="Department Name">
          <TruncatedTextCell maxLength={44} text={department.departmentName} textStyle="bodyLg" />
        </RowGridColumn>
      </GridItem>
      <GridItem colSpan={4} height="100%">
        <RowGridColumn align="flex-start" label="Assignees">
          {department?.deptReplyTo || 'No Assignees'}
        </RowGridColumn>
      </GridItem>
      <GridItem alignItems="center" colSpan={2} height="100%">
        <RowGridColumn label="Active Projects">
          {areStatsLoading ? (
            <LoadingSpinner
              isInline
              spinnerSize="sm"
            />
          ) : (currentDepartmentStats?.activeJobsCount || '0')}
        </RowGridColumn>
      </GridItem>
      <GridItem colSpan={2} height="100%">
        <RowGridColumn label="Active Records">
          {areStatsLoading ? (
            <LoadingSpinner
              isInline
              spinnerSize="sm"
            />
          ) : (currentDepartmentStats?.activeInsuredsCount || '0')}
        </RowGridColumn>
      </GridItem>
      <GridItem colSpan={2} height="100%">
        <RowGridColumn label="Active Policies">
          {areStatsLoading ? (
            <LoadingSpinner
              isInline
              spinnerSize="sm"
            />
          ) : (currentDepartmentStats?.activePoliciesCount || '0')}
        </RowGridColumn>
      </GridItem>
      <GridItem
        alignItems="flex-end"
        colSpan={2}
        display="flex"
        justifyContent="flex-end"
        textAlign="right"
      >
        <IconButtonMenu
          ariaLabel="Department Options"
          dusk={`${department.departmentName}-${analyticsIds.adminSettingsPage.departments.departmentRowOptionsButton}`}
          iconElement={<Icon as={BsThreeDots} boxSize="20px" px={0} />}
          id={department.departmentId}
          isDisabled={!!areStatsLoading}
          menuOptions={menuOptions}
          size="sm"
          width="auto"
        />
      </GridItem>
    </Row>
  );
};

DepartmentRow.propTypes = {
  department: PropTypes.shape({
    archivedAt: PropTypes.instanceOf(Date),
    departmentId: PropTypes.number,
    departmentName: PropTypes.string,
    deptReplyTo: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
  }),
  currentDepartmentStats: PropTypes.shape({
    activePoliciesCount: PropTypes.number,
    activeInsuredsCount: PropTypes.number,
    inactiveJobsCount: PropTypes.number,
    activeJobsCount: PropTypes.number
  })
};

export default DepartmentRow;
